/* <bespoke-request-form>: the form panel of sections/bespoke-request.liquid.
 * Shopify brings a failed post back with its own fields refilled but not custom
 * ones, so Budget and Needed by, folded under "Add a budget or a date", would
 * return empty and closed. On submit the fields in that fold are remembered for
 * the tab (sessionStorage, per form id); when the page comes back with errors
 * they are put back and the fold opens, and a sent request forgets them.
 * Without JavaScript or storage they simply come back empty. */
if (!customElements.get('bespoke-request-form')) {
  const session = (use) => {
    try {
      return use(window.sessionStorage);
    } catch {
      return null;
    }
  };

  customElements.define('bespoke-request-form', class extends HTMLElement {
    connectedCallback() {
      const form = this.querySelector('form');
      const more = this.querySelector('[data-bespoke-request-more]');
      if (!form || !more) return;
      const key = `bespoke-request:${form.id}`;
      const fields = Array.from(more.querySelectorAll('input, textarea'));

      const success = form.querySelector('[data-bespoke-request-success]');
      const errors = form.querySelector('[data-bespoke-request-errors]');
      if (success) {
        session((storage) => storage.removeItem(key));
      } else if (errors) {
        const saved = session((storage) => JSON.parse(storage.getItem(key))) || {};
        fields.forEach((field) => {
          if (!field.value && typeof saved[field.name] === 'string') field.value = saved[field.name];
        });
        if (fields.some((field) => field.value)) more.open = true;
      }
      // Shopify comes back with its marker and, after an error, the posted fields echoed in the
      // query; the message must not stay in the address bar or the history. The hash stays.
      if (success || errors) {
        const url = new URL(location.href);
        Array.from(url.searchParams.keys()).forEach((name) => {
          if (name === 'contact_posted' || name === 'form_type' || name.startsWith('contact[')) url.searchParams.delete(name);
        });
        history.replaceState(history.state, '', url);
      }

      this.events = new AbortController();
      form.addEventListener('submit', () => {
        const filled = fields.filter((field) => field.value.trim());
        session((storage) => {
          if (filled.length) storage.setItem(key, JSON.stringify(Object.fromEntries(filled.map((field) => [field.name, field.value]))));
          else storage.removeItem(key);
        });
      }, { signal: this.events.signal });
    }

    disconnectedCallback() {
      this.events?.abort();
    }
  });
}

/* Phone-only request bar (sections/bespoke-request.liquid). It stands in for the
 * page's own first link to the request section (the opening's button): it shows
 * once that link has scrolled away above the viewport, and only while the
 * request section is still below it, so it never covers the form or the footer.
 * Without such a link on the page it shows after the first screen instead.
 * CSS keeps it off wider screens and hidden until this element is defined. */
if (!customElements.get('bespoke-request-bar')) {
  customElements.define('bespoke-request-bar', class extends HTMLElement {
    connectedCallback() {
      const target = document.getElementById(this.dataset.target);
      if (!target || !('IntersectionObserver' in window)) return;

      const lead = Array.from(document.querySelectorAll(`a[href="#${CSS.escape(target.id)}"]`))
        .find((link) => !this.contains(link));
      if (!lead) {
        this.sentinel = document.createElement('div');
        this.sentinel.className = 'bespoke-request__sentinel';
        this.sentinel.setAttribute('aria-hidden', 'true');
        document.body.prepend(this.sentinel);
      }

      let leadGone = false;
      let reachedTarget = false;
      const update = () => this.toggleAttribute('data-visible', leadGone && !reachedTarget);

      // Roots stretched far below / above the viewport turn "intersecting" into "not yet
      // scrolled above" / "already reached", so a jump in either direction still reports.
      this.leadObserver = new IntersectionObserver(([entry]) => {
        leadGone = !entry.isIntersecting;
        update();
      }, { rootMargin: '0px 0px 100000px 0px' });
      this.targetObserver = new IntersectionObserver(([entry]) => {
        reachedTarget = entry.isIntersecting;
        update();
      }, { rootMargin: '100000px 0px 0px 0px' });

      this.leadObserver.observe(lead || this.sentinel);
      this.targetObserver.observe(target);
    }

    disconnectedCallback() {
      this.leadObserver?.disconnect();
      this.targetObserver?.disconnect();
      this.sentinel?.remove();
    }
  });
}
